/**
 * Map scale arithmetic.
 *
 * A representative fraction (RF) "1:N" says one unit on the map is N of the same unit on
 * the ground. Ground distance = map distance × N; the scale from a measurement is
 * ground ÷ map in the same unit. Lengths go through metres; the inch is exactly 2.54 cm,
 * so 1 mile = 63 360 inches and "1 inch to 1 mile" is 1:63 360.
 */

import { parseNumberInput } from "@/lib/parse-number";

export type LengthUnit =
  | "mm"
  | "cm"
  | "m"
  | "km"
  | "in"
  | "ft"
  | "yd"
  | "mi"
  | "nmi";

export const METRES_PER_UNIT: Record<LengthUnit, number> = {
  mm: 0.001,
  cm: 0.01,
  m: 1,
  km: 1000,
  in: 0.0254,
  ft: 0.3048,
  yd: 0.9144,
  mi: 1609.344,
  nmi: 1852,
};

export function toMetres(value: number, unit: LengthUnit): number {
  return value * METRES_PER_UNIT[unit];
}

export function fromMetres(metres: number, unit: LengthUnit): number {
  return metres / METRES_PER_UNIT[unit];
}

const RATIO_SEPARATOR = /[:/]/;
const GROUPING = /[\s,'’]/g;

/**
 * The N of a scale typed as "1:50000", "1 : 50,000", "1/24000", "50 000" or "2:1".
 * A scale "a:b" is b ÷ a, so "2:1" (twice life size) is N = 0.5. Null if unreadable or not positive.
 */
export function parseScale(text: string): number | null {
  const parts = text.split(RATIO_SEPARATOR);
  if (parts.length > 2) {
    return null;
  }
  const read = (part: string) => {
    const compact = part.replace(GROUPING, "");
    return compact === "" ? null : parseNumberInput(compact);
  };
  if (parts.length === 1) {
    const only = read(parts[0]);
    return only !== null && only > 0 ? only : null;
  }
  const left = read(parts[0]);
  const right = read(parts[1]);
  if (left === null || right === null || left <= 0 || right <= 0) {
    return null;
  }
  return right / left;
}

/** Ground distance (m) for a map distance (m) at scale 1:N. */
export function groundFromMap(mapMetres: number, n: number): number {
  return mapMetres * n;
}

/** Map distance (m) for a ground distance (m) at scale 1:N. */
export function mapFromGround(groundMetres: number, n: number): number {
  return groundMetres / n;
}

/** N from a map distance and a ground distance, both in metres. Null unless both are positive. */
export function scaleFromDistances(
  mapMetres: number,
  groundMetres: number
): number | null {
  if (!(mapMetres > 0 && groundMetres > 0)) {
    return null;
  }
  return groundMetres / mapMetres;
}

/** A ground area (m²) for a map area (m²): the scale applies twice. */
export function groundAreaFromMap(mapSquareMetres: number, n: number): number {
  return mapSquareMetres * n * n;
}

export type UnitSystem = "metric" | "imperial";

/** The most readable unit for a length: m or km, ft or mi. */
export function readableUnit(metres: number, system: UnitSystem): LengthUnit {
  const size = Math.abs(metres);
  if (system === "metric") {
    if (size < 0.01) {
      return "mm";
    }
    if (size < 1) {
      return "cm";
    }
    return size < 1000 ? "m" : "km";
  }
  if (size < 0.0254 * 12) {
    return "in";
  }
  return size < 1609.344 ? "ft" : "mi";
}
